import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import {
  Person2 as Person2Icon,
  Chair as PropIcon,
  Landscape as EnvironmentIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { fetchModels, uploadModel, deleteModel } from '../services/modelService';

const CharacterLibrary = ({ selectedCharacter, onCharacterSelect, onDeleteCharacter }) => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('character');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadCharacters(selectedTab);
  }, [selectedTab]);

  const loadCharacters = async (type) => {
    try {
      setLoading(true);
      const data = await fetchModels(type);
      setCharacters(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError('Failed to load characters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      setUploadError('File size exceeds 100MB limit');
      event.target.value = '';
      return;
    }

    // Validate file type
    const allowedFormats = ['.fbx', '.gltf', '.glb', '.obj'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedFormats.includes(fileExt)) {
      setUploadError('Invalid file format. Only FBX, GLTF, GLB, and OBJ files are allowed.');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadProgress(0);

      const metadata = {
        name: file.name.split('.')[0],
        type: selectedTab,
        scale: 1
      };

      const characterData = await uploadModel(file, metadata, (progress) => {
        setUploadProgress(Math.round(progress));
      });

      await loadCharacters(selectedTab);
      onCharacterSelect?.(characterData);
      
      // Show success message
      setUploadError({ severity: 'success', message: 'Character uploaded successfully!' });
    } catch (err) {
      console.error('Error uploading character:', err);
      setUploadError(err.message || 'Failed to upload character. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear the input value to allow uploading the same file again
      event.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteModel(id);
      await loadCharacters(selectedTab);
      onDeleteCharacter?.(id);
    } catch (err) {
      console.error('Error deleting character:', err);
      setError('Failed to delete character. Please try again.');
    }
  };

  const handleCharacterSelect = (character) => {
    if (onCharacterSelect) {
      onCharacterSelect({
        url: character.url,
        format: character.format || character.url.split('.').pop().toLowerCase(),
        name: character.name,
        type: character.type
      });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        Model Library
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={(e, newValue) => setSelectedTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<Person2Icon />}
          label="Characters"
          value="character"
        />
        <Tab
          icon={<PropIcon />}
          label="Props"
          value="prop"
        />
        <Tab
          icon={<EnvironmentIcon />}
          label="Environment"
          value="environment"
        />
      </Tabs>

      <Box sx={{ p: 1 }}>
        <input
          type="file"
          accept=".fbx,.gltf,.glb,.obj"
          id="character-upload"
          hidden
          onChange={handleUpload}
          disabled={uploading}
        />
        <label htmlFor="character-upload">
          <Button
            component="span"
            variant="outlined"
            startIcon={<UploadIcon />}
            disabled={uploading}
            fullWidth
          >
            Upload {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
          </Button>
        </label>
        {uploading && (
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {uploadError && (
        <Alert 
          severity={typeof uploadError === 'object' ? uploadError.severity : 'error'} 
          onClose={() => setUploadError(null)}
          sx={{ mb: 2 }}
        >
          {typeof uploadError === 'object' ? uploadError.message : uploadError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : characters.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            No {selectedTab}s available. Upload one to get started.
          </Typography>
        ) : (
          characters.map((char) => (
            <ListItem
              key={char.id}
              button
              onClick={() => handleCharacterSelect(char)}
              selected={selectedCharacter?.id === char.id}
            >
              <ListItemText
                primary={char.name}
                secondary={`Format: ${char.format || char.url.split('.').pop().toUpperCase()}`}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCharacter?.(char.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );
};

export default CharacterLibrary;
